import { forwardRef, useImperativeHandle, useState } from 'react';
import { List, ListItemButton, ListItemText, Paper } from '@mui/material';
import type { MentionUser } from '../types';

interface MentionListProps {
  items: MentionUser[];
  command: (item: { id: string; label: string }) => void;
}

export const MentionList = forwardRef((props: MentionListProps, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command({ id: item.id, label: item.name });
    }
  };

  const adjustSelected = (delta: number) => {
    const count = props.items.length;
    if (count === 0) return;
    setSelectedIndex((prevIndex) => {
      const current = prevIndex >= count ? 0 : prevIndex;
      return (current + delta + count) % count;
    });
  };

  const upHandler = () => adjustSelected(-1);
  const downHandler = () => adjustSelected(1);

  const enterHandler = () => {
    const count = props.items.length;
    if (count === 0) return;
    const index = selectedIndex >= count ? 0 : selectedIndex;
    selectItem(index);
  };

  // Avoid calling setState synchronously in an effect (react-hooks/set-state-in-effect).
  // Instead, compute a safe index for rendering and only update state in response
  // to user actions (up/down). This prevents cascading renders when `props.items`
  // changes frequently.
  const safeIndex = props.items.length
    ? selectedIndex >= props.items.length
      ? 0
      : selectedIndex
    : 0;

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }
      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }
      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }
      return false;
    },
  }));

  if (!props.items.length) {
    return null;
  }

  return (
    <Paper elevation={3} sx={{ maxHeight: 200, overflow: 'auto', p: 0 }}>
      <List disablePadding>
        {props.items.map((item, index) => (
          <ListItemButton
            key={item.id}
            onClick={() => selectItem(index)}
            selected={index === safeIndex}
          >
            <ListItemText primary={item.name} />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
});

MentionList.displayName = 'MentionList';
